import { useState, forwardRef, useImperativeHandle } from "react";
import "../../styles/MedicalNotes.css";
import toast from "react-hot-toast";
import { fetchInstance } from "../../helpers/interceptor";

const ALLERGY_KEYS = [
  { key: "Penicillin", render: "AR1C1" },
  { key: "Aspirin", render: "AR1C2" },
  { key: "Novocaine", render: "AR2C3" },
  { key: "Iodine", render: "AR2C1" },
  { key: "Tape", render: "AR2C2" },
  { key: "Codeine", render: "AR2C3" },
  { key: "NKDA", render: "AR3C1" },
];

const FAMILY_KEYS = [
  { key: "High blood pressure", render: "FHR1C1" },
  { key: "Cancer", render: "FHR1C2" },
  { key: "Orthopedic disease", render: "FHR1C3" },
  { key: "Bone or joint problems", render: "FHR2C1" },
  { key: "Bleeding tendencies", render: "FHR2C2" },
  { key: "Diabetes", render: "FHR2C3" },
  { key: "None", render: "FHR3C1" },
];

const CHIEF_PARTS = [
  "Neck", "Shoulder", "Elbow", "Wrist", "Back", "Hip", "Knee", "Ankle"
];

const MED_CONDITIONS = [
  "High blood pressure", "Mitral valve prolapse", "Bleeding disorder",
  "Circulation disorder", "Back pain", "Osteoporosis", "Angina/Chest pain",
  "Angioplasty", "Stroke/TIA's", "Anemia", "Asthma", "Dyslipidemia",
  "Ulcers", "Diabetes", "Arthritis", "Cancer", "Heart attack",
  "Thyroid disorder", "Cirrhosis", "Seizures", "DVT/PE", "Hepatitis",
  "Hiatal hernia", "Kidney disorder", "Depression/Anxiety", "None"
];

function Chk({ on, onClick }) {
  return (
    <div
      role="checkbox"
      aria-checked={on}
      tabIndex={0}
      className={`mn-chk${on ? " mn-chk--on" : ""}`}
      onClick={onClick}
      onKeyDown={e => (e.key === " " || e.key === "Enter") && onClick()}
    >
      {on && (
        <svg width="8" height="8" viewBox="0 0 12 12" fill="none">
          <polyline points="2,6 5,9 10,3" stroke="white" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </div>
  );
}

function Radio({ on, onClick }) {
  return (
    <div
      role="radio"
      aria-checked={on}
      tabIndex={0}
      className={`mn-radio${on ? " mn-radio--on" : ""}`}
      onClick={onClick}
      onKeyDown={e => (e.key === " " || e.key === "Enter") && onClick()}
    >
      {on && <div className="mn-radio-dot" />}
    </div>
  );
}

function buildSimpleData(
  chiefState,
  condState,
  otherPMH,
  priorInjuries,
  allergiesState,
  familyState,
  socialState
) {
  const data = {};

  data.chiefComplaints = {};
  CHIEF_PARTS.forEach(part => {
    const isChecked = !!chiefState.chk?.[part];
    const location = chiefState.loc?.[part] || "";
    const notesText = (chiefState.notes?.[part] || "").trim();

    data.chiefComplaints[part] = isChecked;
    if (["Shoulder", "Elbow", "Wrist", "Hip", "Knee", "Ankle"].includes(part)) {
      data.chiefComplaints[`${part}Left`] = location === "Left";
      data.chiefComplaints[`${part}Right`] = location === "Right";
      data.chiefComplaints[`${part}Bilateral`] = location === "Bilateral";
    }
    data.chiefComplaints[`${part}Text`] = notesText;
  });

  const otherChiefText = (chiefState.otherChiefComplaint || "").trim();
  if (otherChiefText) {
    data.chiefComplaints.OtherText = otherChiefText;
  }

  data.medicalConditions = {};
  MED_CONDITIONS.forEach(c => {
    data.medicalConditions[c] = !!condState[c];
  });
  if (otherPMH.trim()) data.otherMedicalHistory = otherPMH.trim();

  if (priorInjuries.trim()) data.priorInjuries = priorInjuries.trim();

  data.allergies = {};
  ALLERGY_KEYS.forEach(a => {
    data.allergies[a.key] = !!allergiesState[a.key];
  });
  data.allergies.NKDA = !!allergiesState.NKDA;
  data.allergies.Other = !!allergiesState.Other;
  if (allergiesState.otherText?.trim()) {
    data.otherAllergies = allergiesState.otherText.trim();
  }

  data.familyHistory = {};
  FAMILY_KEYS.forEach(f => {
    data.familyHistory[f.key] = !!familyState[f.key];
  });
  if (familyState.other?.trim()) data.otherFamilyHistory = familyState.other.trim();

  const s = socialState;
  data.social = {
    Doesnotsmoke: s.smoking === "none",
    Smoking: s.smoking === "current",
    packsPerDay: s.smoking === "current" ? (s.smokingDetails?.packYears || "") : "",
    packYears: s.smoking === "current" ? (s.smokingDetails?.years || "") : "",
    cigarettesPerDay: s.smoking === "current" ? (s.smokingDetails?.cigarettesPerDay || "") : "",
    cigarettesYears: s.smoking === "current" ? (s.smokingDetails?.cigarettesYears || "") : "",
    QuitSmoking: s.smoking === "quit",
    Quitthisyear: s.smoking === "quit" && s.smokingDetails?.quitDuration === "This year / less than 1 year",
    Quit10years: s.smoking === "quit" && s.smokingDetails?.quitDuration === "10 years",
    Quitmorethan10years: s.smoking === "quit" && s.smokingDetails?.quitDuration === ">10 years",
    Doesntdrinkalcohol: s.alcohol === "none",
    Drinkssocially: s.alcohol === "social",
    Drinks: s.alcohol === "daily",
    alcoholDrinksPerDay: s.alcohol === "daily" ? (s.alcoholDrinksPerDay || "") : "",
    recreationalDrugs: !!s.recreationalDrugs,
    drugsWhat: s.recreationalDrugs ? (s.drugsWhat?.trim() || "") : ""
  };

  return data;
}

function MedicalContent({
  chiefState, setChiefState,
  condState, setCondState,
  familyState, setFamilyState,
  socialState, setSocialState,
  otherPMH, setOtherPMH,
  priorInjuries, setPriorInjuries,
  allergiesState, setAllergiesState
}) {
  const { chk = {}, loc = {}, notes = {}, otherChiefComplaint = "" } = chiefState;

  const toggleChief = (part) => {
    setChiefState(prev => {
      const wasChecked = !!prev.chk?.[part];
      return {
        ...prev,
        chk: { ...prev.chk, [part]: !wasChecked },
        ...(wasChecked ? {
          loc: { ...prev.loc, [part]: undefined },
          notes: { ...prev.notes, [part]: "" }
        } : {})
      };
    });
  };

  const setLocation = (part, value) => {
    if (!chk[part]) return;
    setChiefState(p => ({ ...p, loc: { ...p.loc, [part]: value } }));
  };

  const setNote = (part, value) => {
    if (!chk[part]) return;
    setChiefState(p => ({ ...p, notes: { ...p.notes, [part]: value } }));
  };

  const setOtherChief = (value) => {
    setChiefState(p => ({ ...p, otherChiefComplaint: value }));
  };

  return (
    <div className="mn-content">
      <div className="mn-block">
        <div className="mn-block__header">
          <span className="mn-block__header-title">Chief Complaints</span>
        </div>
        <div className="mn-block__col-header mn-chief-col-header">
          <span className="mn-block__col-label">Body Part</span>
          <span className="mn-block__col-label">Location</span>
          <span className="mn-block__col-label">Notes</span>
        </div>

        {CHIEF_PARTS.map((part, i) => {
          const isOn = !!chk[part];
          const rowBg = isOn ? "rgba(42,157,143,.06)" : (i % 2 === 0 ? "var(--mn-white)" : "var(--mn-s50)");
          const showLocation = ["Shoulder", "Elbow", "Wrist", "Knee", "Ankle", "Hip"].includes(part);

          return (
            <div
              key={part}
              className="mn-chief-row"
              style={{ background: rowBg, borderBottom: i < CHIEF_PARTS.length - 1 ? "1px solid var(--mn-s100)" : "none" }}
            >
              <label className="mn-chief-part-label" style={{ fontWeight: isOn ? 600 : 400, color: isOn ? "var(--mn-t700)" : "var(--mn-dk)" }}>
                <Chk on={isOn} onClick={() => toggleChief(part)} />
                {part}
              </label>
              <div className="mn-chief-locs">
                {showLocation && isOn && ["Left", "Right", "Bilateral"].map(l => (
                  <label key={l} className="mn-chief-loc-label">
                    <Radio
                      on={loc[part] === l}
                      onClick={() => setLocation(part, l)}
                    />
                    {l}
                  </label>
                ))}
              </div>
              <input
                disabled={!isOn}
                placeholder="Notes..."
                className="mn-chief-notes"
                value={notes[part] || ""}
                onChange={e => setNote(part, e.target.value)}
              />
            </div>
          );
        })}

        <div style={{ padding: "12px", borderTop: "1px solid var(--mn-s100)" }}>
          <label style={{ display: "block", marginBottom: "6px", fontWeight: 500 }}>
            Other Chief Complaint:
          </label>
          <input
            type="text"
            className="mn-chief-notes"
            style={{ width: "100%" }}
            placeholder="Enter other chief complaint"
            value={otherChiefComplaint}
            onChange={e => setOtherChief(e.target.value)}
          />
        </div>
      </div>

      <div className="mn-block">
        <div className="mn-block__header">
          <span className="mn-block__header-title">Past Medical History</span>
        </div>
        <div className="mn-history-grid">
          {MED_CONDITIONS.map(c => (
            <label
              key={c}
              className={`mn-history-item${condState[c] ? " mn-history-item--on" : ""}`}
              onClick={() => setCondState(p => ({ ...p, [c]: !p[c] }))}
            >
              <Chk on={!!condState[c]} />
              {c}
            </label>
          ))}
        </div>
        <div style={{ padding: "12px" }}>
          <textarea
            className="mn-chief-notes"
            placeholder="Other past medical history"
            value={otherPMH}
            onChange={e => setOtherPMH(e.target.value)}
            rows={3}
            style={{ width: "100%", resize: "vertical" }}
          />
        </div>
      </div>

      <div className="mn-block">
        <div className="mn-block__header">
          <span className="mn-block__header-title">Prior Injuries</span>
        </div>
        <div style={{ padding: "12px" }}>
          <textarea
            className="mn-chief-notes"
            placeholder="Prior injuries"
            value={priorInjuries}
            onChange={e => setPriorInjuries(e.target.value)}
            rows={4}
            style={{ width: "100%", resize: "vertical" }}
          />
        </div>
      </div>

      <div className="mn-block">
        <div className="mn-block__header">
          <span className="mn-block__header-title">Allergies</span>
        </div>
        <div className="mn-history-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", padding: "12px" }}>
          {["Penicillin", "Aspirin", "Novocaine", "Iodine", "Tape", "Codeine"].map(allergy => (
            <label
              key={allergy}
              className={`mn-history-item${allergiesState[allergy] ? " mn-history-item--on" : ""}`}
              onClick={() => setAllergiesState(prev => ({ ...prev, [allergy]: !prev[allergy] }))}
            >
              <Chk on={!!allergiesState[allergy]} />
              {allergy}
            </label>
          ))}
          <label
            className={`mn-history-item${allergiesState.NKDA ? " mn-history-item--on" : ""}`}
            onClick={() => setAllergiesState(prev => ({ ...prev, NKDA: !prev.NKDA }))}
          >
            <Chk on={!!allergiesState.NKDA} />
            NKDA
          </label>
          <label
            className={`mn-history-item${allergiesState.Other ? " mn-history-item--on" : ""}`}
            onClick={() => setAllergiesState(prev => ({ ...prev, Other: !prev.Other }))}
          >
            <Chk on={!!allergiesState.Other} />
            Other
          </label>
        </div>
        {(allergiesState.Other || Object.values(allergiesState).some(v => v === true && typeof v === 'boolean')) && (
          <div style={{ padding: "0 12px 12px" }}>
            <label style={{ display: "block", marginBottom: "6px", fontWeight: 500 }}>
              Other allergies:
            </label>
            <input
              type="text"
              className="mn-chief-notes"
              style={{ width: "100%" }}
              value={allergiesState.otherText || ""}
              onChange={e => setAllergiesState(p => ({ ...p, otherText: e.target.value }))}
              placeholder="Other allergies"
            />
          </div>
        )}
      </div>

      <div className="mn-block">
        <div className="mn-block__header">
          <span className="mn-block__header-title">Family History</span>
        </div>
        <div className="mn-history-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
          {[
            "High blood pressure",
            "Cancer",
            "Orthopedic disease",
            "Bone or joint problems",
            "Bleeding tendencies",
            "Diabetes",
          ].map(cond => (
            <label
              key={cond}
              className={`mn-history-item${familyState[cond] ? " mn-history-item--on" : ""}`}
              onClick={() => setFamilyState(prev => ({ ...prev, [cond]: !prev[cond] }))}
            >
              <Chk on={!!familyState[cond]} />
              {cond}
            </label>
          ))}
          <label
            className={`mn-history-item${familyState.None ? " mn-history-item--on" : ""}`}
            onClick={() => setFamilyState(prev => ({ ...prev, None: !prev.None }))}
          >
            <Chk on={!!familyState.None} />
            None
          </label>
        </div>
        <div style={{ marginTop: "0px", marginBottom: "10px", padding: "0 12px" }}>
          <label style={{ display: "block", marginBottom: "6px", fontWeight: 500 }}>
            Other family history:
          </label>
          <input
            type="text"
            className="mn-chief-notes"
            style={{ width: "100%" }}
            value={familyState.other || ""}
            onChange={e => setFamilyState(p => ({ ...p, other: e.target.value }))}
            placeholder="Other family history"
          />
        </div>
      </div>

      <div className="mn-block">
        <div className="mn-block__header">
          <span className="mn-block__header-title">Social History</span>
        </div>

        <div style={{ margin: "12px 0", padding: "0 12px" }}>
          <div style={{ fontWeight: 500, marginBottom: 8 }}>Smoking:</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
            {["Doesn’t smoke", "Smoking", "Quit smoking"].map(opt => (
              <label key={opt} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Radio
                  on={socialState.smoking === (opt === "Doesn’t smoke" ? "none" : opt === "Smoking" ? "current" : "quit")}
                  onClick={() => setSocialState(p => ({ ...p, smoking: opt === "Doesn’t smoke" ? "none" : opt === "Smoking" ? "current" : "quit" }))}
                />
                {opt}
              </label>
            ))}
          </div>

          {socialState.smoking === "current" && (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center" }}>
                <div>
                  <input
                    placeholder="Packs per day"
                    value={socialState.smokingDetails?.packYears || ""}
                    onChange={e => setSocialState(p => ({
                      ...p,
                      smokingDetails: { ...p.smokingDetails, packYears: e.target.value }
                    }))}
                    style={{ width: "110px" }}
                    className="mn-chief-notes"
                  />
                  <span style={{ margin: "0 6px" }}>packs/day for</span>
                  <input
                    placeholder="years"
                    value={socialState.smokingDetails?.years || ""}
                    onChange={e => setSocialState(p => ({
                      ...p,
                      smokingDetails: { ...p.smokingDetails, years: e.target.value }
                    }))}
                    style={{ width: "80px" }}
                    className="mn-chief-notes"
                  />
                  <span style={{ marginLeft: "8px" }}>years</span>
                </div>

                <div>
                  <input
                    placeholder="Cigarettes per day"
                    value={socialState.smokingDetails?.cigarettesPerDay || ""}
                    onChange={e => setSocialState(p => ({
                      ...p,
                      smokingDetails: { ...p.smokingDetails, cigarettesPerDay: e.target.value }
                    }))}
                    style={{ width: "130px" }}
                    className="mn-chief-notes"
                  />
                  <span style={{ margin: "0 6px" }}>for</span>
                  <input
                    placeholder="years"
                    value={socialState.smokingDetails?.cigarettesYears || ""}
                    onChange={e => setSocialState(p => ({
                      ...p,
                      smokingDetails: { ...p.smokingDetails, cigarettesYears: e.target.value }
                    }))}
                    style={{ width: "80px" }}
                    className="mn-chief-notes"
                  />
                  <span style={{ marginLeft: "8px" }}>years</span>
                </div>
              </div>
            </div>
          )}

          {socialState.smoking === "quit" && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 500, marginBottom: 6 }}>Quit for:</div>
              {["This year / less than 1 year", "10 years", ">10 years"].map(dur => (
                <label key={dur} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <Radio
                    on={socialState.smokingDetails?.quitDuration === dur}
                    onClick={() => setSocialState(p => ({
                      ...p,
                      smokingDetails: { ...p.smokingDetails, quitDuration: dur }
                    }))}
                  />
                  {dur}
                </label>
              ))}
            </div>
          )}
        </div>

        <div style={{ margin: "20px 0", padding: "0 12px" }}>
          <div style={{ fontWeight: 500, marginBottom: 8 }}>Alcohol:</div>
          <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
            {["Doesn’t drink alcohol", "Drinks alcohol socially", "Drinks"].map(opt => (
              <label key={opt} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Radio
                  on={socialState.alcohol === (opt === "Doesn’t drink alcohol" ? "none" : opt === "Drinks alcohol socially" ? "social" : "daily")}
                  onClick={() => setSocialState(p => ({ ...p, alcohol: opt === "Doesn’t drink alcohol" ? "none" : opt === "Drinks alcohol socially" ? "social" : "daily" }))}
                />
                {opt}
              </label>
            ))}
          </div>
          {socialState.alcohol === "daily" && (
            <div style={{ marginTop: 10 }}>
              <input
                placeholder="e.g. 2"
                value={socialState.alcoholDrinksPerDay || ""}
                onChange={e => setSocialState(p => ({ ...p, alcoholDrinksPerDay: e.target.value }))}
                className="mn-chief-notes"
                style={{ width: "60px" }}
              />
              <span style={{ marginLeft: 8 }}>drinks per day</span>
            </div>
          )}
        </div>

        <div style={{ padding: "0 12px 12px" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 500 }}>
            <Chk
              on={socialState.recreationalDrugs}
              onClick={() => setSocialState(p => ({ ...p, recreationalDrugs: !p.recreationalDrugs }))}
            />
            Recreational drugs
          </label>
          {socialState.recreationalDrugs && (
            <input
              placeholder="Recreational drugs"
              value={socialState.drugsWhat || ""}
              onChange={e => setSocialState(p => ({ ...p, drugsWhat: e.target.value }))}
              className="mn-chief-notes"
              style={{ width: "100%", marginTop: 8 }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

const MedicalNotes = forwardRef(({ onApiSuccess }, ref) => {
  const [chiefState, setChiefState] = useState({ 
    chk: {}, 
    loc: {}, 
    notes: {}, 
    otherChiefComplaint: "" 
  });

  const [condState, setCondState] = useState({});
  const [otherPMH, setOtherPMH] = useState("");
  const [priorInjuries, setPriorInjuries] = useState("");

  const [allergiesState, setAllergiesState] = useState({
    Penicillin: false,
    Aspirin: false,
    Novocaine: false,
    Iodine: false,
    Tape: false,
    Codeine: false,
    NKDA: false,
    Other: false,
    otherText: "",
  });

  const [familyState, setFamilyState] = useState({
    "High blood pressure": false,
    Cancer: false,
    "Orthopedic disease": false,
    "Bone or joint problems": false,
    "Bleeding tendencies": false,
    Diabetes: false,
    None: false,
    other: "",
  });

  const [socialState, setSocialState] = useState({
    smoking: "none",
    smokingDetails: {
      packYears: "",
      years: "",
      cigarettesPerDay: "",
      cigarettesYears: "",
      quitDuration: "",
    },
    alcohol: "none",
    alcoholDrinksPerDay: "",
    recreationalDrugs: false,
    drugsWhat: "",
  });

  useImperativeHandle(ref, () => ({
    saveAndFinish: async () => {
      const simpleData = buildSimpleData(
        chiefState,
        condState,
        otherPMH,
        priorInjuries,
        allergiesState,
        familyState,
        socialState
      );
      let data = sessionStorage.getItem("selectedRow");
      let parsedData = JSON.parse(data);
      let data1 = sessionStorage.getItem("tokenData");
      let parsedData1 = JSON.parse(data1);
      try {
        const response = await fetchInstance(`${process.env.REACT_APP_LAW_API_URL}MedicalNote/save-medical-notes`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Env: process.env.REACT_APP_ENVIRONMENT },
          body: JSON.stringify({
            PatientId: parsedData?.object?.patientID,
            AccountId: parsedData?.object?.accountID,
            UserId: parsedData?.object?.userID,
            LocationId: parsedData1?.providerId,
            DoctorId: parsedData?.object?.physicianID,
            NotesJson: JSON.stringify(simpleData),
            CreatedBy: parsedData?.object?.username
          }),
        });

        const result = await response.json();
        if (response.ok && result.success) {
          toast.success("Medical notes saved successfully!");
        } else {
          toast.error(result.message || "Failed to save notes");
        }
      } catch (err) {
        console.error("API call failed:", err);
        toast.error("Error connecting to server");
      }

      onApiSuccess?.();
    }
  }));

  return (
    <div className="medical-notes">
      <MedicalContent
        chiefState={chiefState}
        setChiefState={setChiefState}
        condState={condState}
        setCondState={setCondState}
        familyState={familyState}
        setFamilyState={setFamilyState}
        socialState={socialState}
        setSocialState={setSocialState}
        otherPMH={otherPMH}
        setOtherPMH={setOtherPMH}
        priorInjuries={priorInjuries}
        setPriorInjuries={setPriorInjuries}
        allergiesState={allergiesState}
        setAllergiesState={setAllergiesState}
      />
    </div>
  );
});

export default MedicalNotes;